import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Button,
  ButtonGroup,
  Card,
  CardContent
} from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import { formatCurrency, formatNumber } from '../utils/utils.ts';

const api = axios.create({ withCredentials: true });

function TopEvents({ current_broker }) {
  const { getAccessTokenSilently } = useAuth0();

  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDays, setSelectedDays] = useState('7d');
  const [selectedSource, setSelectedSource] = useState('inventory');

  const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'];

  const columns = [
    { 
      field: 'event_name', 
      headerName: 'Event Name', 
      type: 'string', 
      flex: 1 
    },

    {
      field: 'event_date',
      headerName: 'Event Date',
      type: 'dateTime',
      width: 154,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format('MM/DD/YYYY') : ''
    },

    {
      field: 'event_time',
      headerName: 'Event Time',
      type: 'dateTime',
      width: 147,
      renderCell: (params) =>
        params.value ? dayjs(params.value).format('hh:mm A') : ''
    },

    { field: 'venue_name', headerName: 'Venue', type: 'string', width: 175 },

    {
      field: 'tickets_sold',
      headerName: 'Tickets Sold',
      type: 'number',
      width: 105
    },

    {
      field: 'gross_revenue',
      headerName: 'Gross Revenue',
      type: 'number',
      width: 182,
      renderCell: (params) => formatCurrency(params.value)
    }
  ];

  const detailColumns = selectedSource === 'exchange'
  ? [
      { field: 'section', headerName: 'Section', width: 600 },
      { field: 'row', headerName: 'Row', width: 100 },
      { field: 'qty', headerName: 'Qty', width: 100 },
      { field: 'total_amount', headerName: 'Total Amount', width: 200,
        valueFormatter: (v) => formatCurrency(v)
      },
      { field: 'avg_price', headerName: 'ATP', width: 100,
        valueFormatter: (v) => formatCurrency(v)
      }
    ]
  : [
      { field: 'section', headerName: 'Section', width: 600 },
      { field: 'row', headerName: 'Row', width: 100 },
      { field: 'qty', headerName: 'Qty', width: 100 },
      { field: 'customer_company_name', headerName: 'Sold To', width: 200 },
      { field: 'invoice_amount', headerName: 'Invoice Amount', width: 200,
        valueFormatter: (v) => formatCurrency(v)
      }
    ];

  const TicketDetailView = ({ row }) => {
    const rows = row.inventory_details.map((r, i) => ({
      id: i,
      section: r.section,
      row: r.row,
      qty: r.qty,
      invoice_amount: r.invoice_amount,
      total_amount: r.total_amount,
      avg_price: r.avg_price,
      customer_company_name: r.customer_company_name
    }));

    return (
      <Box sx={{ height: 300, width: '100%', p: 1 }}>
        <DataGridPro
          rows={rows}
          columns={detailColumns}
          hideFooter
          disableRowSelectionOnClick
        />
      </Box>
    );
  };

  const handleDaysChange = (days) => setSelectedDays(days);
  const handleSourceChange = (source) => setSelectedSource(source);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0AUDIENCE,
          scope: process.env.REACT_APP_AUTH0SCOPE
        });

        if (current_broker) {
          const daysParam = selectedDays.replace('d', '');

          const res = await api.get(
            `${process.env.REACT_APP_BASEURL}/api/top-events-dashboard/top-events/${selectedSource}/${current_broker}/${daysParam}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (isMounted) {
            setData(res.data);
            setSummary(res.data.summary);
            setTrendData(res.data.trend_data);
          }
        } 
      } catch (err) {
        if (isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, current_broker, selectedDays, selectedSource]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 6
        }}
      >
        {selectedSource === 'exchange' && (
          <>
            <Typography
              sx={{
                color: 'error.main',
                fontWeight: 'bold',
                textAlign: 'center',
                maxWidth: 550,
                mb: '20px'
              }}
            >
              Load times for exchange data may extend to approximately one minute.
              We are sorry for the inconvenience and are working on optimizing these times.
            </Typography>

            <CircularProgress />
          </>
        )}

        {selectedSource !== 'exchange' && <CircularProgress />}
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error">
        Error loading data: {JSON.stringify(error)}
      </Typography>
    );
  }

  const rows =
    data?.data?.slice(0, 20).map((item) => {
      const d = item.event_date ? new Date(item.event_date) : null;

      return {
        id: `${item.event_id}_${item.event_date}`,
        event_name: item.event_name,
        venue_name: item.venue_name,
        event_date: d,
        event_time: d,
        tickets_sold: Number(item.tickets_sold),
        gross_revenue: Number(item.gross_revenue),
        inventory_details: item.inventory_details
      };
    }) || [];

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
        <ButtonGroup variant="outlined">
          <Button
            variant={selectedSource === 'inventory' ? 'contained' : 'outlined'}
            onClick={() => handleSourceChange('inventory')}
          >
            My Inventory
          </Button>
          <Button
            variant={selectedSource === 'exchange' ? 'contained' : 'outlined'}
            onClick={() => handleSourceChange('exchange')}
          >
            Exchange
          </Button>
        </ButtonGroup>

        <ButtonGroup variant="outlined">
          <Button 
            variant={selectedDays === '30d' ? 'contained' : 'outlined'} 
            onClick={() => handleDaysChange('30d')}
          >
            30D
          </Button>
          <Button 
            variant={selectedDays === '14d' ? 'contained' : 'outlined'} 
            onClick={() => handleDaysChange('14d')}
          >
            14D
          </Button>
          <Button 
            variant={selectedDays === '7d' ? 'contained' : 'outlined'} 
            onClick={() => handleDaysChange('7d')}
          >
            7D
          </Button>
        </ButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', minHeight: 320 }}>

        <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>

          <Card sx={{ flex: 1, display: 'flex', borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6">Total Volume ({selectedDays})</Typography>
              <Typography fontSize={32}>
                {formatNumber(summary?.total_volume)}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, display: 'flex', borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6">Total Gross ({selectedDays})</Typography>
              <Typography fontSize={32}>
                {formatCurrency(summary?.total_gross)}
              </Typography>
            </CardContent>
          </Card>

        </Box>

        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Trend - Top 5 Events
              </Typography>

              <Box sx={{ height: 320 }}>
                {trendData?.series?.length ? (
                  <LineChart
                    xAxis={[{
                      data: trendData.dates.map(d =>
                        dayjs(d).format('MM/DD')
                      ),
                      scaleType: 'band'
                    }]}
                    series={trendData.series.map((s, i) => ({
                      data: s.data,
                      label: s.name,
                      color: colors[i % colors.length],
                      showMark: false
                    }))}
                    slotProps={{
                      legend: { hidden: true }
                    }}
                    height={300}
                  />
                ) : (
                  <Typography>No trend data</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Top Events Leaderboard
        </Typography>

        <Box sx={{ height: 750 }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            getDetailPanelContent={({ row }) => (
              <TicketDetailView row={row} />
            )}
            getDetailPanelHeight={() => 'auto'}
            pageSize={20}
            rowsPerPageOptions={[20]}
            hideFooter
            disableSelectionOnClick
            components={{ Toolbar: GridToolbar }}
          />
        </Box>
      </Paper>

    </Box>
  );
}

export default TopEvents;